import {middleTruncate, nanoid, Paths, readNestedProperty} from "@2d-front-monorepo/core";
import React, {Children, HTMLAttributes, JSX, memo, ReactElement, ReactNode, useMemo, useRef} from "react";
import {useTableScrollbarMetrics} from "../helpers/useTableScrollbarMetrics";
import {columnType} from "./functions";
import {TableColumnProps} from "./TableColumn";
import {TableColumnType} from "./TableColumnType";

export interface TableProps<T extends object> {
	caption?: string,
	children: ReactElement<TableColumnProps<T, any, any>>[];
	className?: string;
	data: T[];
	responsive?: boolean;
	rowProps?: (row: T, index: number) => HTMLAttributes<HTMLTableRowElement>;
}

// Column-config keys consumed by the Table itself; everything else on a cell
// column's props is forwarded to the cell component (e.g., onEdit/onDelete).
const COLUMN_CONFIG_KEYS = new Set(["align", "className", "title", "type", "property", "cell", "render", "surrogate"]);

// Everything about a column that is invariant across rows, computed once per
// render (or once per column array when the caller memoizes it) instead of
// once per cell.
interface ColumnMeta<T extends object> {
	props: TableColumnProps<T, any, any>;
	key: React.Key;
	className: string;
	dataTitle: string | undefined;
	extra: Record<string, unknown> | undefined;
}

function renderTruncatedText(value: string): ReactNode {
	const [head, tail] = middleTruncate(value);
	return (
		<div>
			<span>{head}</span>
			{tail ? <span>{tail}</span> : null}
		</div>
	);
}

function renderCell<T extends object>(meta: ColumnMeta<T>, row: T, index: number): ReactNode {
	const props = meta.props;
	const value = readNestedProperty(row, props.property as Paths<T>);

	if ("render" in props && props.render) {
		return props.render(row, value as never);
	}

	if ("cell" in props && props.cell) {
		const Cell = props.cell;
		return <Cell row={row} cell={value} index={index} {...meta.extra}/>;
	}

	const cellValue = props.surrogate ? props.surrogate(value as never) : (value as ReactNode);
	return <>{props.type === TableColumnType.TruncatedText && typeof cellValue === "string" ? renderTruncatedText(cellValue) : cellValue}</>;
}

interface TableRowProps<T extends object> {
	row: T;
	index: number;
	columns: ColumnMeta<T>[];
	rowProps?: (row: T, index: number) => HTMLAttributes<HTMLTableRowElement>;
}

// Memoized so a re-render that keeps the same row objects (e.g., the loading flip
// of a refetching) skips every unchanged row. Callers that pass a stable columns
// array (mbasico memoizes it per schema) get full benefit; inline children still
// work, they just re-render as before.
const TableRow = memo(function TableRow<T extends object>(props: TableRowProps<T>): JSX.Element {
	const {row, index, columns, rowProps} = props;
	return (
		<tr {...rowProps?.(row, index)}>{
			columns.map(meta => (
				<td key={meta.key}
				    className={meta.className}
				    data-title={meta.dataTitle}>
					{meta.props.type === TableColumnType.Actions
						? <span>{renderCell(meta, row, index)}</span>
						: renderCell(meta, row, index)
					}
				</td>
			))
		}</tr>
	);
}) as <T extends object>(props: TableRowProps<T>) => JSX.Element;

export default function Table<T extends object>(props: TableProps<T>): JSX.Element {
	const {
		data,
		children,
		caption,
		className,
		responsive,
		rowProps
	} = props;

	const actualClassName = useMemo(() => `${className ?? ""} ${responsive ? "table--responsive" : "table--fixed"}`.trim(), [className, responsive]);
	const captionID = useMemo(() => "caption-" + nanoid(), []);

	const columnMeta = useMemo<ColumnMeta<T>[]>(() => {
		const columns = Children.toArray(children) as React.ReactElement<TableColumnProps<T, any, any>>[];
		return columns.map((column, i) => {
			const columnProps = column.props;
			let extra: Record<string, unknown> | undefined;
			if ("cell" in columnProps && columnProps.cell) {
				extra = {};
				for (const key in columnProps) {
					if (!COLUMN_CONFIG_KEYS.has(key)) {
						extra[key] = (columnProps as Record<string, unknown>)[key];
					}
				}
			}
			return {
				props: columnProps,
				key: column.key ?? i,
				className: columnType(columnProps),
				dataTitle: responsive && typeof columnProps.title === "string" ? columnProps.title : undefined,
				extra
			};
		});
	}, [children, responsive]);

	// Publishes this region's own vertical-scrollbar metrics (--table-scrollbar-inset
	// for the cue, --table-vbar for the right-edge spacing). See useTableScrollbarMetrics.
	const regionRef = useRef<HTMLDivElement>(null);
	useTableScrollbarMetrics(regionRef);

	return <div ref={regionRef} role="region" tabIndex={0} aria-labelledby={caption ? captionID : undefined} className={actualClassName}>
		<table>
			{caption ? <caption id={captionID}>{caption}</caption> : null}
			<thead>
			<tr>
				{columnMeta.map(meta => {
					return (
						<th role="columnheader"
						    scope="col"
						    key={meta.key}
						    className={meta.className}>
							{meta.props.title}
						</th>);
				})}
			</tr>
			</thead>
			<tbody>
			{
				data.map((row, i) => <TableRow key={i} row={row} index={i} columns={columnMeta} rowProps={rowProps}/>)
			}
			</tbody>
		</table>
	</div>;
}
